/*
 *   File : training.js
 *   Author : https://evoqins.com
 *   Description :  Training detail
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';

//Custom component
import { DataTableComponent } from '../../../../Component/DataTable';
import { SummaryCard } from '../../../../Component/Cards';
import { APIService } from '../../../../Service';
import { Loader } from '../../../../Component/Others';
import { EmptyScreen } from '../../../../Component/EmptyComponent';

const TABLE_CATEGORY_TAB = [
    {
        id: 1,
        name: "Resources"
    },
    {
        id: 2,
        name: "Videos"
    },
    {
        id: 3,
        name: "Quiz"
    },
    // {
    //     id: 4,
    //     name: "Flash quiz"
    // },
];

const ITEMS_PER_PAGE = 15;

const SORT_FIELDS_BY_FILTER = {
    1: ['course_id', 'course_name', 'completed_at', 'points'],
    2: ['video_id', 'video_name', 'watched_on', 'points'],
    3: ['quiz_id', 'quiz_name', 'points', 'played_on'],
    4: ['quiz_id', 'quiz_name', 'points', 'played_on'],
};

const Training = (props) => {

    const [tabIndex, setTabIndex] = useState(1);

    const [trainingData, setTrainingData] = useState({});
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [tableLoading, setTableLoading] = useState(true);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }

        _getTrainingDetail();
    }, [tabIndex, currentPage]);

    // handle tab change 
    const _handleTabChange = (id) => {
        if (id === tabIndex) {
            return;
        }

        setTabIndex(id);
        setSortField(null);
        setSortDirection(null);
        setTableLoading(true);

        // Always fetch with cleared sort for the new filter; skip the list useEffect
        // so it does not re-call with a stale sortField from the previous tab.
        skipListFetchRef.current = true;
        setCurrentPage(1);
        _getTrainingDetail(1, null, null, id);
    };

    // handle resource table page change 
    const _handleTablePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setTableLoading(true);
    };

    const _getAllowedSortFields = (filter = tabIndex) => {
        return SORT_FIELDS_BY_FILTER[filter] || [];
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;
        const allowedFields = _getAllowedSortFields();

        if (!sortBy || !allowedFields.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setTableLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getTrainingDetail(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // resource table 
    const TABLE_COLUMNS_RESOURCES = [
        {
            name: 'Resource name',
            id: 'course_name',
            sortField: 'course_name',
            selector: row => row.course_name,
            width: "460px",
            sortable: true,
        },
        {
            name: 'Completed on',
            id: 'completed_at',
            sortField: 'completed_at',
            selector: row => row.completed_at.length === 0 ? '-' : row.completed_at,
            width: "160px",
            sortable: true,
        },
        {
            name: "Earned points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points,
            sortable: true,
        }
    ];

    // video data table 
    const TABLE_COLUMNS_VIDEOS = [
        {
            name: 'Watched videos name',
            id: 'video_name',
            sortField: 'video_name',
            selector: row => row.video_name,
            width: "400px",
            sortable: true,
        },
        {
            name: 'Watched on',
            id: 'watched_on',
            sortField: 'watched_on',
            selector: row => row.watched_on,
            width: "160px",
            sortable: true,
        },
        {
            name: "Earned points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points,
            sortable: true,
        }

    ];

    // quiz data table 
    const TABLE_COLUMNS_QUIZ = [
        {
            name: 'Quiz names',
            id: 'quiz_name',
            sortField: 'quiz_name',
            selector: row => row.quiz_name,
            width: "400px",
            sortable: true,
        },
        {
            name: 'Played on',
            id: 'played_on',
            sortField: 'played_on',
            selector: row => row.played_on,
            width: "160px",
            sortable: true,
        },
        {
            name: "Earned points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points,
            sortable: true,
        }
    ];

    // flash quiz tata table 
    const TABLE_COLUMNS_FLASH_QUIZ = [
        {
            name: 'Quiz names',
            id: 'quiz_name',
            sortField: 'quiz_name',
            selector: row => row.quiz_name,
            width: "400px",
            sortable: true,
        },
        {
            name: 'Played on',
            id: 'played_on',
            sortField: 'played_on',
            selector: row => row.played_on,
            width: "160px",
            sortable: true,
        },
        {
            name: "Earned points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points,
            sortable: true,
        }

    ];

    // API - Get sales specialist training data
    const _getTrainingDetail = (
        page_num = currentPage,
        sort_by = sortField,
        sort_order = sortDirection,
        filter = tabIndex
    ) => {
        const allowedFields = _getAllowedSortFields(filter);
        const effective_sort_by = sort_by && allowedFields.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        let is_post = true;
        let url = 'CA/sales-specialist/training';
        let data = {
            "customer_id": props.customerId,
            "filter": filter,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setTrainingData(response.data);
            } else { }
            setLoading(false);
            setTableLoading(false);
        });
    };

    const tableSortProps = {
        pagination: true,
        paginationServer: true,
        paginationPerPage: ITEMS_PER_PAGE,
        paginationTotalRows: trainingData?.data?.total_count,
        paginationDefaultPage: currentPage,
        paginationResetDefaultPage: false,
        sortServer: true,
        defaultSortFieldId: sortField,
        defaultSortAsc: sortDirection !== 'desc',
        onSort: _handleSort,
        onChangePage: _handleTablePageChange,
    };

    return (
        <>
            {/* Summary  */}
            <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                    Training summary
                </h4>
                <div className='row g-3 row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 padding-10px-top'>
                    {/* Summary cards */}

                    <div className='col'>
                        <SummaryCard title="Ongoing resources"
                            loader={loading}
                            quantity={trainingData.ongoing_course} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Completed resources"
                            loader={loading}
                            quantity={trainingData.completed_course} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Quiz"
                            loader={loading}
                            quantity={trainingData.completed_quiz} />
                    </div>
                    <div className='col'>
                        <SummaryCard title="Videos watched"
                            loader={loading}
                            quantity={trainingData.watched_videos} />
                    </div>
                    {/* <div className='col'>
                        <SummaryCard title="Flash quiz"
                            loader={loading}
                            quantity={trainingData.completed_flash_quiz} />
                    </div> */}
                </div>
            </div>

            {/* Courses created table */}
            <div className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                <h4 className='mb-3 px-4 e-text-rich-black e-alt-font-noto-sans e-font-18 e-font-weight-500 line-height-18px'>
                    Training details
                </h4>

                {/* table category tab */}
                <div className='d-flex gap-0-16 px-4 overflow-auto pb-xl-0 pb-lg-0 pb-1 e-tab-scroll'>
                    {TABLE_CATEGORY_TAB.map((data, key) => (
                        <div key={key} className={`${tabIndex === data.id ? 'e-bg-lust e-text-white pe-none' : 'e-text-rich-black'} e-border-1px-cool-grey e-border-radius-24 cursor-pointer`}
                            onClick={() => _handleTabChange(data.id)} >
                            <p className='mb-0 px-4 padding-12px-tb e-alt-font-noto-sans  e-font-14 e-font-weight-500 line-height-14px w-max-content'>
                                {data.name}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Data table */}
                <div className='e-table'>
                    {/* data table resources */}
                    {tabIndex === 1 ?
                        <>
                            {tableLoading ?
                                // loader 
                                <Loader />
                                :
                                <>
                                    {trainingData.data.data.length !== 0 ?
                                        <DataTableComponent
                                            key={`training-resources-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                            columns={TABLE_COLUMNS_RESOURCES}
                                            data={trainingData.data.data}
                                            {...tableSortProps} />
                                        :
                                        // empty screen
                                        <div className='h-100vh pt-5 mt-5'>
                                            <EmptyScreen message="No data found" />
                                        </div>
                                    }
                                </>
                            }
                        </>
                        :
                        //data table videos
                        tabIndex === 2 ?
                            <>
                                {tableLoading ?
                                    // loader 
                                    <Loader />
                                    :
                                    <>
                                        {trainingData.data.data.length !== 0 ?
                                            <DataTableComponent
                                                key={`training-videos-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                columns={TABLE_COLUMNS_VIDEOS}
                                                data={trainingData.data.data}
                                                {...tableSortProps} />
                                            :
                                            // empty screen
                                            <div className='h-100vh pt-5 mt-5'>
                                                <EmptyScreen message="No data found" />
                                            </div>
                                        }
                                    </>
                                }
                            </>
                            :
                            //data table quiz
                            tabIndex === 3 ?
                                <>
                                    {tableLoading ?
                                        // loader 
                                        <Loader />
                                        :
                                        <>
                                            {trainingData.data.data.length !== 0 ?
                                                <DataTableComponent
                                                    key={`training-quiz-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                    columns={TABLE_COLUMNS_QUIZ}
                                                    data={trainingData.data.data}
                                                    {...tableSortProps} />
                                                :
                                                // empty screen
                                                <div className='h-100vh pt-5 mt-5'>
                                                    <EmptyScreen message="No data found" />
                                                </div>
                                            }
                                        </>
                                    }
                                </>
                                :
                                <>
                                    {tableLoading ?
                                        // loader 
                                        <Loader />
                                        :
                                        <>
                                            {trainingData.data.data.length !== 0 ?
                                                <DataTableComponent
                                                    key={`training-flash-quiz-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                    columns={TABLE_COLUMNS_FLASH_QUIZ}
                                                    data={trainingData.data.data}
                                                    {...tableSortProps} />
                                                :
                                                // empty screen
                                                <div className='h-100vh pt-5 mt-5'>
                                                    <EmptyScreen message="No data found" />
                                                </div>
                                            }
                                        </>
                                    }
                                </>
                    }
                </div>
            </div>
        </>
    )
}

export default Training;
